import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./RegisterStyle.css";

const Register = () => {
  // Form state management
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  // UI state management
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [formTouched, setFormTouched] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const { register } = useAuth();
  const navigate = useNavigate();

  // Password strength calculator
  const calculatePasswordStrength = (password) => {
    if (!password) return "";

    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 10) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength <= 2) return "weak";
    if (strength <= 3) return "medium";
    return "strong";
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Update password strength on password change
    if (name === "password") {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    // Clear error when user starts typing
    if (error && !formTouched) {
      setFormTouched(true);
    }
  };

  // Form validation
  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Please enter your full name");
      return false;
    }

    if (!formData.email.trim()) {
      setError("Please enter your email address");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await register(formData.name, formData.email, formData.password);

      // Check if registration requires email verification
      if (response.message && response.message.includes("verify")) {
        setRegistrationSuccess(true);
        setSuccessMessage(response.message);
        localStorage.setItem("pendingVerificationEmail", formData.email);
      } else {
        // Old flow - direct login
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container-main">
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-10 col-xl-9">
            <div className="row g-0 register-card-shadow register-rounded-modern overflow-hidden register-fade-in">
              {/* Left Side - Branding & Features */}
              <div className="col-md-5 register-gradient-bg text-white p-5 d-flex flex-column justify-content-between">
                <div>
                  <div className="register-brand-section mb-4">
                    <i className="fa fa-file-pdf-o register-brand-icon mb-3"></i>
                    <h2 className="fw-bold mb-3 register-brand-title">Join PDF API</h2>
                    <p className="register-brand-description mb-4">
                      Start converting your documents today with our powerful and secure platform.
                    </p>
                  </div>

                  {/* Features List */}
                  <div className="register-features-list">
                    <div className="register-feature-item mb-3">
                      <i className="fa fa-check-circle fa-lg me-3"></i>
                      <span>Free trial with 1 conversion</span>
                    </div>
                    <div className="register-feature-item mb-3">
                      <i className="fa fa-shield fa-lg me-3"></i>
                      <span>256-bit SSL encryption</span>
                    </div>
                    <div className="register-feature-item mb-3">
                      <i className="fa fa-credit-card fa-lg me-3"></i>
                      <span>No credit card required</span>
                    </div>
                    <div className="register-feature-item mb-3">
                      <i className="fa fa-rocket fa-lg me-3"></i>
                      <span>Instant API access</span>
                    </div>
                    <div className="register-feature-item">
                      <i className="fa fa-users fa-lg me-3"></i>
                      <span>Join 10,000+ developers</span>
                    </div>
                  </div>
                </div>

                {/* Trust Badge */}
                <div className="register-trust-badge mt-4">
                  <i className="fa fa-lock me-2"></i>
                  <span>Your data is secure with us</span>
                </div>
              </div>

              {/* Right Side - Registration Form */}
              <div className="col-md-7 bg-white p-5">
                {!registrationSuccess ? (
                  <>
                    <div className="register-form-header mb-4">
                      <h3 className="fw-bold mb-2">
                        <i className="fa fa-user-plus register-header-icon me-2"></i>
                        Create Your Account
                      </h3>
                      <p className="text-muted register-header-subtitle">
                        Get started with your free account in seconds
                      </p>
                    </div>

                    {/* Error Alert */}
                    {error && (
                      <div className="register-alert-danger mb-3">
                        <i className="fa fa-exclamation-circle me-2"></i>
                        <span>{error}</span>
                      </div>
                    )}

                <form onSubmit={handleSubmit} noValidate>
                  {/* Full Name Field */}
                  <div className="mb-3">
                    <label className="register-form-label fw-semibold">
                      <i className="fa fa-user text-muted me-2"></i>
                      Full Name
                    </label>
                    <div className="register-input-group">
                      <input
                        type="text"
                        className="form-control register-input-modern"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        required
                        autoComplete="name"
                      />
                    </div>
                  </div>

                  {/* Email Field */}
                  <div className="mb-3">
                    <label className="register-form-label fw-semibold">
                      <i className="fa fa-envelope text-muted me-2"></i>
                      Email Address
                    </label>
                    <div className="register-input-group">
                      <input
                        type="email"
                        className="form-control register-input-modern"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        required
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="mb-3">
                    <label className="register-form-label fw-semibold">
                      <i className="fa fa-lock text-muted me-2"></i>
                      Password
                    </label>
                    <div className="register-input-group position-relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control register-input-modern"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="At least 6 characters"
                        required
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="register-password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex="-1"
                      >
                        <i className={`fa fa-eye${showPassword ? "-slash" : ""}`}></i>
                      </button>
                    </div>
                    {/* Password Strength Indicator */}
                    {formData.password && (
                      <div className={`register-password-strength ${passwordStrength}`}>
                        <div className="register-strength-bar"></div>
                      </div>
                    )}
                    {passwordStrength && (
                      <small className={`register-strength-text register-strength-${passwordStrength}`}>
                        Password strength: {passwordStrength}
                      </small>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div className="mb-4">
                    <label className="register-form-label fw-semibold">
                      <i className="fa fa-lock text-muted me-2"></i>
                      Confirm Password
                    </label>
                    <div className="register-input-group position-relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        className="form-control register-input-modern"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Re-enter your password"
                        required
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="register-password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        tabIndex="-1"
                      >
                        <i className={`fa fa-eye${showConfirmPassword ? "-slash" : ""}`}></i>
                      </button>
                    </div>
                    {/* Password Match Indicator */}
                    {formData.confirmPassword && formData.password && (
                      <small className={`d-flex align-items-center mt-2 ${
                        formData.password === formData.confirmPassword
                          ? "text-success"
                          : "text-danger"
                      }`}>
                        <i className={`fa fa-${
                          formData.password === formData.confirmPassword
                            ? "check-circle"
                            : "times-circle"
                        } me-1`}></i>
                        {formData.password === formData.confirmPassword
                          ? "Passwords match"
                          : "Passwords do not match"}
                      </small>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="btn register-btn-primary w-100 mb-3"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Creating Your Account...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-rocket me-2"></i>
                        Create Account
                      </>
                    )}
                  </button>

                  {/* Sign In Link */}
                  <div className="text-center register-signin-link">
                    <p className="text-muted mb-0">
                      Already have an account?{" "}
                      <Link to="/login" className="register-link fw-semibold">
                        Sign in here
                        <i className="fa fa-arrow-right ms-1"></i>
                      </Link>
                    </p>
                  </div>
                </form>

                    {/* Terms and Privacy */}
                    <div className="register-terms-section mt-4 pt-3 border-top">
                      <p className="text-muted small mb-0 text-center">
                        <i className="fa fa-info-circle me-1"></i>
                        By creating an account, you agree to our{" "}
                        <a href="#terms" className="register-link">Terms of Service</a>
                        {" "}and{" "}
                        <a href="#privacy" className="register-link">Privacy Policy</a>
                      </p>
                    </div>
                  </>
                ) : (
                  /* Success Message */
                  <div className="register-success-content text-center">
                    <div className="register-success-icon-wrapper mb-4">
                      <i className="fa fa-envelope-open register-success-icon"></i>
                    </div>
                    <h3 className="fw-bold mb-3">
                      Check Your Email!
                    </h3>
                    <p className="text-muted mb-4">
                      {successMessage}
                    </p>

                    <div className="register-email-info-box mb-4">
                      <p className="mb-2">
                        <i className="fa fa-check text-success me-2"></i>
                        We sent a verification link to
                      </p>
                      <p className="fw-bold text-primary mb-0">
                        {formData.email}
                      </p>
                    </div>

                    <div className="register-next-steps mb-4">
                      <h5 className="mb-3">
                        <i className="fa fa-list-ol me-2"></i>
                        What's Next?
                      </h5>
                      <div className="register-step-item">
                        <span className="register-step-number">1</span>
                        <span>Check your email inbox</span>
                      </div>
                      <div className="register-step-item">
                        <span className="register-step-number">2</span>
                        <span>Click the verification link</span>
                      </div>
                      <div className="register-step-item">
                        <span className="register-step-number">3</span>
                        <span>Start using PDF API!</span>
                      </div>
                    </div>

                    <div className="register-help-text">
                      <p className="text-muted small mb-3">
                        <i className="fa fa-info-circle me-1"></i>
                        Didn't receive the email? Check your spam folder or{" "}
                        <Link to="/verify-email" className="register-link">resend verification</Link>
                      </p>
                    </div>

                    <Link to="/login" className="btn register-btn-outline w-100">
                      <i className="fa fa-arrow-left me-2"></i>
                      Back to Login
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
